import { Col, Row, Form } from "antd"
import { FormField } from "../../../components/Form/FormField"
import { useTranslation } from "react-i18next"
import { observer } from "mobx-react"
import { InputType } from "../../../components/TextInput/const/InputType.enum"
import { useContainerInjection } from "../../../hooks/useContainerInjection"
import { SurveyFormData, SurveysViewModel } from "../SurveysViewModel"
import MultimediaUploader from "../../../components/MultimediaUploader/MultimediaUploader"
import { colors } from "../../../assets/styles/appStyles"
import { useState } from "react"
import { UploadFile } from "antd/lib"

interface SurveyInformationFormSectionProps {
	formData: SurveyFormData
	onChange: (formData: Partial<SurveyFormData>) => void
	showAdminRecommendation?: boolean
}

const SurveyInformationForm = ({
	formData,
	onChange,
	showAdminRecommendation = false
}: SurveyInformationFormSectionProps) => {
	const { t } = useTranslation("surveys", { keyPrefix: "form.surveyInfo" })
	const { t: tForm } = useTranslation("mainForm", { keyPrefix: "validations" })
	const viewModel = useContainerInjection<SurveysViewModel>("SurveysViewModel")
	const [showImageError, setShowImageError] = useState(false)
	const activeMarketplaceIds = viewModel.surveys.flatMap(survey => survey.marketplaces ?? [])

	const activeMicrositeIds = viewModel.surveys.flatMap(survey => survey.microsites?.map(m => m.id) ?? [])

	const activeIdsSet = new Set([...activeMarketplaceIds, ...activeMicrositeIds])

	const buildOptions = () => {
		const options = [
			{ id: "ES", label: `Marketplace ES`, value: "ES" },
			{ id: "MX", label: `Marketplace MX`, value: "MX" },
			...viewModel.microsites.map(microsite => ({
				id: microsite.id!,
				value: microsite.id!,
				label: microsite.slug
			}))
		]
		return options.filter(option => !activeIdsSet.has(option.id))
	}

	const siteOptions = buildOptions()

	return (
		<>
			{!showAdminRecommendation && (
				<Row gutter={[24, 0]}>
					<Col
						span={24}
						xl={14}
						md={24}
						xs={24}
						style={{ display: "flex", flexDirection: "column" }}
						className="pb-4"
					>
						{formData.id && <FormField.Input key={"id"} label={t("id")} name={"id"} disabled={true} />}
						<FormField.Input
							key={"title"}
							label={t("title")}
							name={"title"}
							mandatory
							placeHolder={t("titlePlaceholder")}
							onChange={(title: string) => onChange({ title })}
						/>
						<FormField.Input
							key={"title_en"}
							label={`${t("title")}  (EN)`}
							name={"title_en"}
							mandatory
							placeHolder={t("titlePlaceholderEN")}
							onChange={(title_en: string) => onChange({ title_en })}
						/>
						<FormField.Input
							key={"title_ca"}
							label={`${t("title")}  (CA)`}
							name={"title_ca"}
							mandatory
							placeHolder={t("titlePlaceholderCA")}
							onChange={(title_ca: string) => onChange({ title_ca })}
						/>

						<FormField.Input
							key={"description"}
							label={t("description")}
							name={"description"}
							inputType={InputType.TEXTAREA}
							placeHolder={t("descriptionPlaceholder")}
							onChange={(description: string) => onChange({ description })}
						/>
						<FormField.Input
							key={"description_en"}
							label={`${t("description")} (EN) `}
							name={"description_en"}
							inputType={InputType.TEXTAREA}
							placeHolder={t("descriptionPlaceholderEN")}
							onChange={(description_en: string) => onChange({ description_en })}
						/>
						<FormField.Input
							key={"description_ca"}
							label={`${t("description")} (CA) `}
							name={"description_ca"}
							inputType={InputType.TEXTAREA}
							placeHolder={t("descriptionPlaceholderCA")}
							onChange={(description_ca: string) => onChange({ description_ca })}
						/>

						<FormField.SelectSearch
							key={"microsites"}
							label={t("microsite")}
							value={[
								...(formData.marketplaces ?? []).map(marketplace => ({
									value: marketplace,
									label: `Marketplace ${marketplace}`
								})),
								...(formData.microsites?.map(microsite => ({
									value: microsite.id,
									label: microsite.slug
								})) ?? [])
							]}
							mandatory
							placeholder={t("micrositePlaceholder")}
							mode="multiple"
							onChange={(values: any) => {
								onChange({
									marketplaces: values
										.filter((v: any) => v.value === "ES" || v.value === "MX")
										.map((v: any) => v.value),
									microsites: values
										.filter((v: any) => v.value !== "ES" && v.value !== "MX")
										.map((v: any) =>
											viewModel.microsites.find(microsite => microsite.id === v.value)
										)
								})
							}}
							options={siteOptions}
						/>
					</Col>
					<Col xl={10} md={24} xs={24}>
						<Form.Item
							label={t("image")}
							name={"image"}
							rules={[
								{
									required: true,
									message: tForm("mandatoryField"),
									validator: async (_, value) => {
										setShowImageError(!!!value)
										if (!value) {
											return Promise.reject(tForm("mandatoryField"))
										}
										return Promise.resolve()
									}
								}
							]}
						>
							<MultimediaUploader
								containerStyle={{
									width: "300px",
									height: "300px",
									overflow: "hidden",
									border: `${showImageError ? `1px solid ${colors.error}` : ""}`
								}}
								mediaUrl={formData.image as string & File}
								type="image"
								onClean={() =>
									onChange({
										...formData,
										image: undefined
									})
								}
								manualUpload
								label={t("uploadImage")}
								onChange={(image: UploadFile) => {
									onChange({ ...formData, image })
								}}
								maxDimensions={{ width: 600, height: 300 }}
							/>
						</Form.Item>
					</Col>
				</Row>
			)}
			{showAdminRecommendation && (
				<Row gutter={[24, 0]}>
					<Col span={24}>
						<FormField.Input
							key={"adminRecommendation"}
							name={"adminRecommendation"}
							inputType={InputType.TEXTAREA}
							onChange={(adminRecommendation: string) => onChange({ adminRecommendation })}
						/>
					</Col>
				</Row>
			)}
		</>
	)
}

export default observer(SurveyInformationForm)
